// AI assisted development
/**
 * React Query Hooks for Feedback API
 * Feature: 012-user-feedback
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { feedbackApi } from '../services/feedbackApi';
import type {
  FeedbackItem,
  CreateFeedbackReplyRequest,
  CreateFeedbackRequest,
  UpdateFeedbackRequest,
  UpdateStatusRequest,
  FeedbackListResponse,
} from '../types/feedback';

/**
 * Hook to fetch all feedback items.
 */
export function useFeedback(status?: string): UseQueryResult<FeedbackListResponse> {
  return useQuery({
    queryKey: ['feedback', status],
    queryFn: () => feedbackApi.getAll(status),
    staleTime: 30 * 1000, // 30 seconds - feedback changes frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single feedback item by ID.
 */
export function useFeedbackItem(id: number | null): UseQueryResult<FeedbackItem> {
  return useQuery({
    queryKey: ['feedback', id],
    queryFn: () => feedbackApi.getById(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create new feedback with optimistic updates.
 */
export function useCreateFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateFeedbackRequest) => feedbackApi.create(request),
    onSuccess: () => {
      // Invalidate and refetch feedback list
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: (error) => {
      // Error handling - React Query will expose error via mutation.error
      console.error('Failed to create feedback:', error);
    },
  });
}

/**
 * Hook to update feedback (author only) with optimistic updates.
 */
export function useUpdateFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateFeedbackRequest }) =>
      feedbackApi.update(id, request),
    onSuccess: (data, variables) => {
      // Update the specific item in cache
      queryClient.setQueryData(['feedback', variables.id], data);
      // Invalidate list to refresh vote counts and other changes
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: (error) => {
      console.error('Failed to update feedback:', error);
    },
  });
}

/**
 * Hook to update feedback status with optimistic updates.
 */
export function useUpdateFeedbackStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateStatusRequest }) =>
      feedbackApi.updateStatus(id, request),
    onSuccess: (data, variables) => {
      // Optimistically update the specific item in cache
      queryClient.setQueryData(['feedback', variables.id], data);
      // Invalidate list to refresh
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: (error) => {
      console.error('Failed to update feedback status:', error);
    },
  });
}

/**
 * Hook to reply to feedback.
 */
export function useCreateFeedbackReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: CreateFeedbackReplyRequest }) =>
      feedbackApi.createReply(id, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feedback', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: (error) => {
      console.error('Failed to reply to feedback:', error);
    },
  });
}

/**
 * Hook to vote on feedback with optimistic updates.
 */
export function useVoteFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => feedbackApi.vote(id),
    onSuccess: (data, id) => {
      // Optimistically update the feedback item in cache
      queryClient.setQueryData<FeedbackItem>(['feedback', id], (old) => {
        if (!old) return old;
        return {
          ...old,
          voteCount: data.voteCount,
          userHasVoted: true,
        };
      });
      
      // Also update the item in all list query caches for every status filter.
      // This provides seamless UI updates without waiting for refetch
      queryClient.setQueriesData<FeedbackListResponse>(
        { queryKey: ['feedback'] },
        (oldData) => {
          if (!oldData?.items) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((item) =>
              item.id === id
                ? { ...item, voteCount: data.voteCount, userHasVoted: true }
                : item
            ),
          };
        }
      );
      
      // Invalidate to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: (error) => {
      console.error('Failed to vote on feedback:', error);
    },
  });
}

